import React, {useEffect} from 'react'
import Modal from 'react-bootstrap/Modal'
import {useDispatch, useSelector} from 'react-redux'
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { DevTool } from "react-hook-form-devtools";
import * as yup from 'yup';
import FormGroup from 'components/Form/FormGroup'
import Button from 'react-bootstrap/Button'
import { deleteForm, saveForm } from 'store/actions/formActions'
import SweetAlert from 'react-bootstrap-sweetalert'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import IconButton from '@material-ui/core/IconButton'
import EditIcon from '@material-ui/icons/Edit';
import analytics from 'util/analytics';
import Spinner from 'react-bootstrap/Spinner'
import {useLocation} from 'util/router'

const textFieldSizeOptions = [
    {
        label: "Small (fits one line of text)",
        value: "sm"
    },
    {
        label: "Medium (fits small paragraph)",
        value: "md"
    }
]

export const processServices = (services) => {
    return services.sort((a,b) => {
        if (a.experience_type === 'Appointment' && b.experience_type !== 'Appointment') {
            return 1
        } else if (a.experience_type !== 'Appointment' && b.experience_type === 'Appointment') {
            return -1
        }
        if (a.experience_type > b.experience_type) {
            return 1;
        } else if (a.experience_type < b.experience_type) { 
            return -1;
        }
    
        // Else go to the 2nd item
        if (a.name < b.name) { 
            return -1;
        } else if (a.name > b.name) {
            return 1
        } else { // nothing to split them
            return 0;
        }
    }).map(val => {
        return {
            ...val,
            experience_type: val.experience_type === 'Appointment' ? 'Private' : val.experience_type
        }
    }).map(val => {
        return { value: val.id, label: val.name + " (" + val.experience_type + ")" }
    })
}

function FormEditor(props) {

    const offerings = useSelector(state => state.instructor.offerings.filter(val => !val.deleted))
    const offeringOptions = offerings.sort((a,b) => {
        if (a.name < b.name) { 
            return -1;
        } else if (a.name > b.name) {
            return 1
        } else { // nothing to split them
            return 0;
        }
    }).map(val => {
        return {
            label: val.name,
            value: val.id
        }
    })

    const serviceOptions = useSelector(state => processServices(state.scheduling.services))

    const dispatch = useDispatch()

    const { toEdit } = props


    // const defualtToEdit = {...toEdit}
    const { register, control, handleSubmit, setValue, formState, errors, dirty, getValues, watch } = useForm({
        mode: 'onBlur',
        validationSchema: yup.object({

            name: yup.string().required("Please choose a name for this form"),
            applies_to_services: yup.array(),
            applies_to_offerings: yup.array(),

            fields: yup.array().min(1)
        }),
        defaultValues: {
            id: toEdit?.id,

            name: toEdit?.name,
            applies_to_services: toEdit?.applies_to_services ?? [],
            applies_to_offerings: toEdit?.applies_to_offerings ?? [],

            fields: toEdit?.fields ?? []
        }
    });

    const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
        control,
        name: "fields"
    });
    useEffect(() => {
        register({ name: 'id'})
    }, [register])

    const [isSaving, setIsSaving] = React.useState(false)
    const [saveError, setSaveError] = React.useState(null)

    console.log("Errors: ", errors)
    const onSubmit = (data) => {


        const newData = {
            ...toEdit,
            ...data
        }

        if (newData.fields?.length === 0) {
            window.alert("Must include at least 1 form field")
            return
        }

        // console.log("Submitting new form data: ", newData)
        // return

        setIsSaving(true)
        dispatch(saveForm(newData))
            .then(result => {
                if (result.success === false && result.message) {
                    throw result.message
                }
                setIsSaving(false)
                if (props.onHide) {
                    props.onHide()
                }
            })
            .catch(err => {
                setIsSaving(false)
                setSaveError(err)
                window.alert(err)
            })
        
    }
    
    const {discount_type, applies_to_all, redemption_type} = watch()

    // switch on type
    // fill viewModel placeholders with placeholders for each type
    // need to swap out "count" for "frequency" on the subscription side.
    return (
        <>
        <form onSubmit={handleSubmit(onSubmit)}>

            <FormGroup
                type="text"
                name="name"
                label="Form Name"
                sublabel="A code may only contain alphanumeric characters"
                placeholder={"ex: Referall Form"}
                register={register}
                error={errors.name}
                fullSize
            />

            <FormGroup
                // className={applies_to_all ? "d-none" : ""}
                error={errors.applies_to_services}
                fullSize
                select
                isMulti={true}
                options={serviceOptions}
                type="text"
                name="applies_to_services"
                label="Show for these templates"
                placeholder="Select some or none"
                control={control}
                setValue={setValue}
                getValues={getValues}
            />

            <FormGroup
                // className={applies_to_all ? "d-none" : ""}
                error={errors.applies_to_offerings}
                fullSize
                select
                isMulti={true}
                options={offeringOptions}
                type="text"
                name="applies_to_offerings"
                label="Show for these pricing options &amp; communities"
                placeholder="Select some or none"
                control={control}
                setValue={setValue}
                getValues={getValues}
            />

            <hr></hr>
            <h4>Questions:</h4>
            
            {fields.map((item, index) => (
                <div key={item.id}>
                    <FormGroup
                        type="text"
                        name={`fields[${index}].question`}
                        label={"Question"}
                        placeholder={"ex: What's your name?"}
                        register={register}
                        // error={errors.name}
                        fullSize
                    />
                    <FormGroup
                        // className={applies_to_all ? "d-none" : ""}
                        // error={errors.applies_to_services}
                        fullSize
                        select
                        // isMulti={true}
                        options={textFieldSizeOptions}
                        type="text"
                        name={`fields[${index}].size`}
                        label="Text field size"
                        placeholder="Select one..."
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                    />

                    <FormGroup
                        fullSize
                        type="checkbox"
                        name={`fields[${index}].isRequired`}
                        label="Required"
                        placeholder="Check this box if you would like this field to be required at checkout"
                        // prepending={["sutrapro.com"]}
                        register={register}
                    />

                    <button type="button" className="btn btn-outline-danger mb-3" onClick={() => {
                        remove(index)
                    }}>
                        Remove Field
                    </button>


                    
                    <hr></hr>
                </div>
            ))}

            <button type="button" className="btn btn-primary mb-3" onClick={() => {
                append({
                    question: "",
                    size: "sm",
                    isRequired: false
                })
            }}>
                Add new field
            </button>

            <Modal.Footer>
                <Button type="submit" variant="primary" disabled={isSaving}>
                    <span>Save changes</span>
                    {isSaving && (
                        <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden={true}
                        className="ml-2"
                      >
                        <span className="sr-only">Sending...</span>
                      </Spinner>

                    )}
                </Button>
            </Modal.Footer>
            {/* <div className="row">
                
            </div> */}

        </form>
        <DevTool control={control} />
        </>

    )
}

function FormEditorModal(props) {

    const {show, onHide, toEdit} = props

    var viewModel = {
        title: toEdit ? "Edit Form" : "Create New Form",
        description: "" 
        // (<span className="font-weight-bold">Please read &amp; watch <a target="_blank" href="http://help.sutrapro.com/en/articles/4663915-promo-codes-how-to">the FAQs</a> before adding a promo code.</span>)
    }

    return(
        <Modal
            centered
            // className={"modal-container custom-map-modal"}
            size="xl"
            // {...props}
            show={show}
            onHide={onHide}
            >
                <Modal.Header closeButton>
                    {viewModel.title}
                </Modal.Header>
                <Modal.Body>
                    {/* <p>A package is a group of classes that is typically discounted when a customer purchases multiple quantities. Packages do not expire and can be used on any live class.</p> */}
                    <p className="mb-5"><big>{viewModel.description}</big></p>
                    <FormEditor
                    {...props}
                    toEdit={toEdit}
                    />
                </Modal.Body>
                
                
        </Modal>
    )
}

function NewFormButton(props) {

    const location = useLocation()

    console.log("FORM BUTTON LOCATION: ", location)

    const [open, setOpen] = React.useState(false)
    // const [newSubOpen, setNewSubOpen] = React.useState(false)
    // const [newChallengeOpen, setNewChallengeOpen] = React.useState(false)
    // const [newOneTimeOfferOpen, setNewOneTimeOfferOpen] = React.useState(false)
    return (
        <div>
            <FormEditorModal
            {...props}
            pack
            show={open}
            onHide={() => setOpen(false)}
            />

            <Button onClick={() => {
                setOpen(true)
                analytics.track("Clicked Add New Form")
            }} >
                Add New&nbsp;+
            </Button>
        </div>
    );
}

export default NewFormButton

function EditFormButton(props) {

    const [open, setOpen] = React.useState(false)
    return (
        <div>

            <FormEditorModal 
            {...props} // includes toEdit
            show={open}
            onHide={() => setOpen(false)}
            />
            <IconButton onClick={() => {
                if (!props.toEdit) {
                    window.alert("Unable to edit class")
                }
                setOpen(true)
            }}>
                <EditIcon/>    
            </IconButton>
        </div>
    );
}

export { EditFormButton }

function DeleteFormButton(props) {

    const [alert, setAlert] = React.useState(false)
    const { toDelete } = props

    const dispatch = useDispatch()

    function handleDelete() {

        console.log("Deleting form")
        dispatch(deleteForm(toDelete))
            .then(res => {
                setAlert(false)
            })
    }

    function confirmDelete() {

        setAlert(
            <SweetAlert
            showCancel
            confirmBtnText="Remove"
            confirmBtnBsStyle="danger"
            title="Are you sure you would like to remove?"
            onConfirm={() => handleDelete()}
            onCancel={() => setAlert(null)}
            focusCancelBtn
            >
             If you do, you can no longer sell this option.
            </SweetAlert>
        )
    }
 
    return (
        <div>
            {alert}
            <IconButton style={{color: "#ff4136"}} onClick={() => confirmDelete()}>
                <DeleteOutlineIcon/>    
            </IconButton>
        </div>
    );
}

export {DeleteFormButton }